/**
 * Tunable knobs for the arrival / taxi / tow / departure motion simulation.
 *
 * These are demo-time controls (the MotionSettingsPanel can mutate them live).
 * All durations are in SIM milliseconds; all distances/speeds in feet.
 *
 * The animation layer is a pure function of the timeline `currentTime`, so
 * changing any of these and recomputing the movement plans is enough — there
 * are no in-flight tweens to cancel.
 */
import { SLOT_MS } from './eventWindow'

/** How an area lets aircraft reach their spot. */
export type ParkingMode = 'self_park' | 'tow_only' | 'both'

/** The end-to-end procedure for one placement. */
export type Procedure = 'self_park' | 'drop_then_tow' | 'direct_tow'

export interface MotionConfig {
  /** Master switch — off restores the legacy snap behavior. */
  enableAnimation: boolean
  /** Enable tug/tow procedures (Stage 3). When off, everything self-parks. */
  enableTow: boolean
  /** Enable the descending final / climb-out airborne segment (Stage 2). */
  enableAirborneFinal: boolean
  /** Draw the taxi/tow corridor under moving aircraft (Stage 4). */
  enablePathRibbons: boolean

  /**
   * Pace 3D model inserts through a cache-aware queue so a burst of arrivals
   * doesn't instantiate dozens of models in one frame. OFF by default — planes
   * appear immediately; turn on only if a dense arrival rush hitches the
   * renderer. Delays (ms) are the spacing between inserts when enabled.
   */
  throttleModelInsertion: boolean
  modelInsertCachedDelayMs: number
  modelInsertLoadDelayMs: number

  /** Sim-time lead-in before `arrival` over which the inbound motion plays. */
  approachLeadMs: number
  /** Sim-time tail-out after `departure` over which the outbound motion plays. */
  exitTailMs: number

  /** Straight-in/out taxi distance (feet) for the v1 self-park path. */
  taxiRunFt: number

  /** Airborne final: altitude at the start of the descent (feet). */
  finalAltFt: number
  /** Airborne final: ground distance of the descending segment (feet). */
  finalRunFt: number
  /** Fraction of the lead-in / tail-out window spent airborne (rest is taxi). */
  airborneFraction: number

  /**
   * Use the runway surface: insert a takeoff-roll (accelerate down the runway
   * before rotation) and a landing-rollout (touch down, decelerate, exit) leg.
   * Requires an authored runway; with the synth graph the roll points collapse
   * onto the thresholds and these legs are skipped automatically.
   */
  enableRunwayRoll: boolean
  /** Touchdown distance past the threshold, feet. */
  touchdownZoneFt: number
  /** Fraction of runway length used for the landing rollout. */
  rolloutFraction: number
  /** Fraction of runway length used for the takeoff roll before rotation. */
  takeoffRollFraction: number
  /** Fraction of the airborne budget spent on the on-runway roll (rest is descent/climb). */
  rollFraction: number

  /** Drop-off / attach / detach dwell durations (sim ms). */
  dropoffDwellMs: number
  tugCoupleDwellMs: number

  /**
   * Fuel trucks: dispatch a truck from the fuel farm to each parked aircraft that
   * requested fuel, dwell while "fueling" (scaled to gallons), then return. Pure
   * function of time, like the tug — see appendFuelTruckLegs.
   */
  enableFuelTrucks: boolean
  /** Fixed setup/teardown portion of the fueling dwell (sim ms). */
  fuelBaseDwellMs: number
  /** Added fueling-dwell sim-ms per requested gallon (scales 200–2000 gal). */
  fuelMsPerGallon: number
  /** Wait after `arrival` before a truck is dispatched (sim ms). */
  fuelTruckBufferMs: number

  /** Per-area parking policy, keyed by areaId. Falls back to `defaultParkingMode`. */
  perAreaParkingMode: Record<string, ParkingMode>
  defaultParkingMode: ParkingMode
  /** For `both` areas: how to pick self-park vs tow. */
  bothModeRule: 'by_size' | 'by_vip' | 'always_tow' | 'always_self'
  /** Wingspan (feet) at/above which `by_size` chooses tow. */
  towWingspanFt: number
}

export const DEFAULT_MOTION_CONFIG: MotionConfig = {
  // Default OFF: planner opens with planes rendered statically on their spots;
  // the toolbar Animation toggle (or Motion panel "Animate") turns motion on.
  enableAnimation: false,
  enableTow: true,
  enableAirborneFinal: true,
  enablePathRibbons: false, // Stage 4 turns this on

  // OFF by default: planes appear instantly. Enable to pace dense arrival rushes.
  throttleModelInsertion: false,
  modelInsertCachedDelayMs: 100,
  modelInsertLoadDelayMs: 500,

  // Wide windows so many aircraft are in motion at once (esp. the Sunday exodus)
  // and each glide spans more sim-time (slower, more watchable). Tunable live.
  approachLeadMs: SLOT_MS * 4, // ~1h
  exitTailMs: SLOT_MS * 8, // ~2h — the exodus drains as a visible flow

  taxiRunFt: 500,

  // Realistic 3° glide path: ~3 km final from ~515 ft (tan 3° × 9850 ft ≈ 516 ft).
  finalAltFt: 515,
  finalRunFt: 9850, // ≈ 3 km
  airborneFraction: 0.55, // more of the lead-in airborne so the longer final isn't time-compressed

  enableRunwayRoll: true,
  touchdownZoneFt: 300,
  rolloutFraction: 0.5,
  takeoffRollFraction: 0.55,
  rollFraction: 0.3,

  dropoffDwellMs: SLOT_MS * 0.5,
  tugCoupleDwellMs: SLOT_MS * 0.25,

  enableFuelTrucks: true,
  fuelBaseDwellMs: SLOT_MS * 0.3, // ~4.5 min fixed
  fuelMsPerGallon: SLOT_MS / 2000, // 2000 gal → +1 slot (~15 min); 200 gal → +1.5 min
  fuelTruckBufferMs: SLOT_MS * 0.5, // dispatch ~7.5 min after the aircraft parks

  perAreaParkingMode: {},
  // Default to a realistic mix: mid-size+ jets get towed, light aircraft self-park.
  defaultParkingMode: 'both',
  bothModeRule: 'by_size',
  towWingspanFt: 60,
}
